import {
  getAccessToken,
  useCreateWallet,
  usePrivy,
  WalletWithMetadata,
} from '@privy-io/react-auth';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import DottedTexture from '@/assets/branding/dotted-texture.svg?react';
import Logo from '@/assets/branding/logo.svg?react';
import TicketsIcon from '@/assets/icons/tickets.svg';
import { Dialog, DialogContent } from '@/components/ui';
import { Button } from '@/components/ui/button';
import Copyable from '@/components/ui/copyable';
import { ROUTES } from '@/config/routes';
import useReferrals from '@/features/referrals/api/get-referrals';
import useRewards from '@/features/referrals/api/get-rewards';
import { useCapitalPlans } from '@/features/vest-capital/api/get-capital-plans';
import LiquidationTable from '@/features/vest-capital/components/liquidation-table';
import { getPrivyPrimaryAddress } from '@/hooks';
import useFundedAccount from '@/hooks/use-funded-account';
import { getCapitalStatus } from '@/lib/api/get-capital-status';
import { privateApi } from '@/lib/api-clients/rest-client';
import { logConsole } from '@/lib/utils';
import { useAccountStore } from '@/store/use-account-store';
import { useCapitalStore } from '@/store/use-capital-store';
import { useModalStore } from '@/store/use-modal-store';
import { CapitalStatus, ErrorCode, ErrorMsgs, getErrorMsg } from '@/types';
import { cn } from '@/utils/cn';
import { centsToDollars, formatNumber } from '@/utils/value-format';

const COMPENSATION_MESSAGE = `Funded Account Compensation Request
(Write the details here...)`;
const BLOCKED_SUPPORT_MESSAGE = `Funded Account Blocked
(Write the details here...)`;

export default function CapitalResultModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { showNewMessage } = useIntercom();
  const { data: referrals } = useReferrals();
  const { data: rewardsData } = useRewards();
  const discountCount = useCapitalStore((state) => state.discountGroups.length);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFreeMode, setIsFreeMode] = useState<boolean>(false);

  const isOpen = useModalStore((state) => state.isCapitalResultModalOpen);

  const { address, capitalStatus, capitalStatusByAddress, setAddress } =
    useAccountStore(
      useShallow((state) => ({
        address: state.address,
        capitalStatus: state.getCapitalStatus(state.address),
        capitalStatusByAddress: state.capitalStatusByAddress,
        setAddress: state.setAddress,
      })),
    );

  const { data: capitalPlans } = useCapitalPlans();
  const { capitalStatusData } = useFundedAccount();
  const { createWallet } = useCreateWallet();

  const isGoalPassed = capitalStatus === CapitalStatus.GOAL_PASSED;
  const isBlocked =
    capitalStatus === CapitalStatus.BLOCKED ||
    capitalStatus === CapitalStatus.FUNDED_BLOCKED;
  const wasFunded =
    capitalStatus === CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS ||
    capitalStatus === CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN;
  const referralLink = `https://trade.vestmarkets.com/join/${referrals?.ref_code}`;

  const accountSize = useMemo(() => {
    if (!address || !capitalStatusData) {
      return 0;
    }

    const plan = capitalPlans?.find(
      (plan) =>
        plan.name ===
        capitalStatusData[address.toLowerCase()]?.active_capital_plan,
    );

    return centsToDollars(plan?.metadata?.account_size ?? '0');
  }, [address, capitalPlans, capitalStatusData]);

  const switchToPrimary = (options?: { redirectDeposit?: boolean }) => {
    useModalStore.setState({ isCapitalResultModalOpen: false });
    setAddress(getPrivyPrimaryAddress(user));
    if (options?.redirectDeposit) {
      navigate(ROUTES.DEPOSIT);
    }
  };

  async function claim() {
    if (!isGoalPassed || !address) {
      return;
    }

    setIsLoading(true);

    let fundedAddress: string;
    // NOTE: check if there's a free wallet first
    const freeWalletAddress = (
      user?.linkedAccounts.find(
        (acc) =>
          acc.type === 'wallet' &&
          capitalStatusByAddress[acc.address.toLowerCase()] ===
            CapitalStatus.PRE_FUNDED,
      ) as WalletWithMetadata
    )?.address;

    const { setAddress, setCapitalStatus } = useAccountStore.getState();

    try {
      if (!freeWalletAddress) {
        fundedAddress = (await createWallet({ createAdditional: true }))
          .address;
        // NOTE: immediately update new wallet to PRE_FUNDED to avoid creating multiple wallets
        // this status will persist in storage, multiple wallets could only be created by claiming on different device after failure from BE
        setCapitalStatus(CapitalStatus.PRE_FUNDED, fundedAddress);
      } else {
        fundedAddress = freeWalletAddress;
      }

      const accessToken = await getAccessToken();
      await privateApi.post('/capital/activateFunded', {
        funded_register_params: {
          primaryAddr: fundedAddress.toLowerCase(),
          privyAccessToken: accessToken,
          isFunded: true,
        },
      });

      logConsole(false, '[Funded Claim]: Awaiting status change');

      // NOTE: wait for status update from ws to not get 'created' on new funded
      await new Promise<void>((res, rej) => {
        // eslint-disable-next-line prefer-const
        let timeout: number;
        // eslint-disable-next-line prefer-const
        let unsubscribe: () => void;

        const cleanup = () => {
          unsubscribe();
          window.clearTimeout(timeout);
        };

        unsubscribe = useAccountStore.subscribe((state) => {
          const status = state.getCapitalStatus(address);
          if (status !== CapitalStatus.FUNDED_CLAIMED) return;
          cleanup();
          res();
        });

        timeout = window.setTimeout(() => {
          getCapitalStatus({ addresses: [address] })
            .then((response) => {
              if (
                response[address].capital_status ===
                CapitalStatus.FUNDED_CLAIMED
              ) {
                res();
              } else {
                rej(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
              }
            })
            .catch(() => rej(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT))
            .finally(cleanup);
        }, 15_000);
      });

      logConsole(
        false,
        "[Funded Claim]: Status changed to 'claimed', switching account to funded",
      );

      setCapitalStatus(CapitalStatus.FUNDED, fundedAddress);

      useModalStore.setState({ isCapitalResultModalOpen: false });
      setAddress(fundedAddress);
      navigate(ROUTES.TRADE);
    } catch (err) {
      if (typeof err === 'number' && err in ErrorMsgs) {
        toast.error(getErrorMsg(err, t));
      } else {
        const error =
          (err as AxiosError<{ detail: { msg: string } }>).response?.data
            ?.detail.msg ??
          (err as Error).message ??
          'Unknown error';
        console.error('[Funded Account Claim] Error occurred:', error);
        toast.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setIsFreeMode(false);
        }
      }}
    >
      <DialogContent
        hideCloseButton={!isFreeMode}
        className={cn(
          'no-scrollbar max-h-full gap-10 overflow-hidden overflow-y-auto border-none sm:max-w-[440px] sm:!rounded-3xl',
          isFreeMode && 'bg-card',
        )}
      >
        <div
          className={cn(
            'relative flex flex-col gap-5 p-5 pb-0 pt-8 sm:p-8 sm:pb-0',
            isFreeMode && 'sm:p-14 sm:pb-0',
          )}
        >
          <DottedTexture
            className={cn(
              'pointer-events-none absolute top-0 z-[-1] h-auto w-full scale-[1.7]',
              isFreeMode
                ? 'opacity-50 [&_ellipse]:fill-[#65B482]'
                : isBlocked
                  ? '[&_ellipse]:fill-red'
                  : !isGoalPassed
                    ? '[&_ellipse]:fill-[#3B66FC]'
                    : undefined,
            )}
          />

          <div className="text-center text-4xl font-semibold capitalize leading-[130%]">
            {isFreeMode && (
              <Logo className="mx-auto mb-8 h-auto w-[65px] [&_path]:fill-foreground" />
            )}

            {isFreeMode
              ? rewardsData?.next_referral_discount === 100
                ? t('capitalResultModal.getFreeRetry')
                : t('capitalResultModal.getADiscoutForNextAccount')
              : isBlocked
                ? t('capitalResultModal.blocked')
                : isGoalPassed
                  ? t('capitalResultModal.congratulations')
                  : t('capitalResultModal.fortuneFavorsTheBold')}
          </div>

          {isFreeMode ? (
            <div className="text-center text-xl text-muted-foreground">
              {rewardsData?.next_referral_discount === 100
                ? t('capitalResultModal.getFreeRetryMessage')
                : t('capitalResultModal.getADiscoutForNextAccountMessage', {
                    discount: rewardsData?.next_referral_discount ?? 20,
                  })}
            </div>
          ) : isBlocked ? (
            <p className="text-center text-xl text-muted-foreground">
              {t('capitalResultModal.blockedMessage')}{' '}
              <Link
                to="/terms"
                className="underline"
                onClick={() => switchToPrimary()}
              >
                {t('capitalResultModal.platformRules')}
              </Link>
              .
              <br />
              <br />
              {t('capitalResultModal.contactSupportMessage')}
            </p>
          ) : isGoalPassed ? (
            <div className="text-center text-xl text-muted-foreground">
              {t('capitalResultModal.youPassedExamination')}
              <br />
              {formatNumber(accountSize, { style: 'currency' })}{' '}
              {t('capitalResultModal.willBeSentToAccount')}.
            </div>
          ) : (
            <>
              <div className="text-center text-xl text-muted-foreground">
                {t('capitalResultModal.justNotThisTime')}
                <br />
                {t('capitalResultModal.theMarketDidntPlayAlong')}
                <br />
                <br />
                {(() => {
                  switch (capitalStatus) {
                    case CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS:
                    case CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS:
                      return t('capitalResultModal.maxDailyLossLimit');
                    case CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN:
                    case CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN:
                      return t('capitalResultModal.maxDrawdownLimit');
                  }
                })()}{' '}
                {t('capitalResultModal.wasExceeded')},{' '}
                {wasFunded
                  ? t('capitalResultModal.fundedAccountHasBeenLiquidated')
                  : t('capitalResultModal.failingEvaluation')}
                .<br />
                {t('capitalResultModal.tryAgain')}?
              </div>

              <LiquidationTable />
            </>
          )}
        </div>

        <div
          className={cn(
            'flex flex-col gap-3 p-5 pt-0 sm:p-8 sm:pt-0',
            isFreeMode && 'sm:p-14 sm:pt-0',
          )}
        >
          {isFreeMode ? (
            <div className="flex flex-col gap-10">
              <Copyable
                variant="button"
                text={t('portfolio.copyInviteLink')}
                buttonProps={{
                  size: 'lg',
                  variant: 'contrast',
                  className: 'capitalize',
                }}
              >
                {referralLink}
              </Copyable>

              <div className="text-lg text-muted-foreground">
                <span className="font-semibold text-foreground">1.</span>{' '}
                <Trans
                  i18nKey="capitalResultModal.getFreeRetryStep1"
                  components={{
                    b: <b className="font-semibold text-foreground" />,
                  }}
                />
                <br />
                <span className="font-semibold text-foreground">2.</span>{' '}
                <Trans
                  i18nKey="capitalResultModal.getFreeRetryStep2"
                  values={{
                    discount: rewardsData?.next_referral_discount ?? 100,
                  }}
                  components={{
                    b: <b className="font-semibold text-foreground" />,
                  }}
                />
              </div>
            </div>
          ) : isBlocked ? (
            <>
              <Button
                variant="contrast"
                size="lg"
                onClick={() => switchToPrimary()}
              >
                {t('capitalResultModal.understand')}
              </Button>

              <Button
                variant="outline2"
                size="lg"
                onClick={() => showNewMessage(BLOCKED_SUPPORT_MESSAGE)}
              >
                {t('capitalResultModal.contactSupport')}
              </Button>
            </>
          ) : isGoalPassed ? (
            <Button
              variant="contrast"
              size="lg"
              loading={isLoading}
              onClick={claim}
            >
              {t('capitalResultModal.goToTrade')}
            </Button>
          ) : (
            <>
              <Button
                variant="contrast"
                size="lg"
                onClick={() => setIsFreeMode(true)}
              >
                {t(
                  rewardsData?.next_referral_discount === 100
                    ? 'capitalResultModal.getFreeRetry'
                    : 'portfolio.unlockADiscount',
                )}
              </Button>

              <Button
                variant="secondary"
                size="lg"
                onClick={() => {
                  useModalStore.setState({ isCapitalResultModalOpen: false });

                  if (discountCount > 0) {
                    useModalStore.setState({ isDiscountsModalOpen: true });
                  }

                  navigate(`${ROUTES.VEST_CAPITAL}#capital-plans`);
                }}
              >
                {t('capitalResultModal.tryAgain')}

                {discountCount > 0 && (
                  <span className="flex items-center gap-1 rounded-md bg-muted-foreground/30 py-1 pl-2 pr-3">
                    <img
                      src={TicketsIcon}
                      alt="Tickets"
                      className="h-auto w-6 max-w-none"
                    />
                    <span className="font-medium">{discountCount}</span>
                  </span>
                )}
              </Button>

              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  useModalStore.setState({ isCapitalResultModalOpen: false });
                  navigate(
                    `${ROUTES.PORTFOLIO}?view=order-history#portfolio-tables`,
                  );
                }}
              >
                {t('capitalResultModal.checkHistory')}
              </Button>

              <Button
                variant="ghost"
                size="lg"
                onClick={() => showNewMessage(COMPENSATION_MESSAGE)}
              >
                {t('capitalResultModal.requestCompensation')}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
